//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * AIErrorHandler.tsx
 *
 * This module provides error handling utilities for AI integration responses.
 * It includes functions to format provider names, extract error information, and generate appropriate error messages.
 * Result of extracting error information from an error response
 */

export interface AIErrorInfo {
  integrationName: string;
  modelName: string;
  errorDetails: string;
  errorType: string;
}

/**
 * Formats a provider name into a user-friendly display name
 *
 * @param providerName - The raw provider name from the API
 * @returns A formatted, user-friendly provider name
 */
export const formatProviderName = (providerName: string): string => {
  if (!providerName) return 'AI Service';

  const lowerCaseName = providerName.toLowerCase();

  if (
    lowerCaseName.includes('azure_openai') ||
    lowerCaseName.includes('azure')
  ) {
    return 'Azure OpenAI';
  } else if (lowerCaseName.includes('openai')) {
    return 'OpenAI';
  } else if (lowerCaseName.includes('ollama')) {
    return 'Ollama';
  } else if (
    lowerCaseName.includes('ibm_watsonx') ||
    lowerCaseName.includes('watsonx')
  ) {
    return 'IBM WatsonX';
  }

  return '';
};

/**
 * Extracts integration information from an error response object
 *
 * @param errorResponse - The error response from the AI service
 * @returns An object containing extracted integration name, model name, and error details
 */
export const extractErrorInfo = (errorResponse: any): AIErrorInfo => {
  const result: AIErrorInfo = {
    integrationName: '',
    modelName: '',
    errorDetails: '',
    errorType: '',
  };

  if (!errorResponse) return result;

  try {
    const providerName = errorResponse.model_provider;
    // Only call formatProviderName if providerName is not undefined/null
    result.integrationName = providerName
      ? formatProviderName(providerName)
      : '';
    if (errorResponse.model) {
      result.modelName = errorResponse.model;
    }
    if (errorResponse.details) {
      result.errorDetails = errorResponse.details;
    } else if (errorResponse.message) {
      result.errorDetails = errorResponse.message;
    }

    if (errorResponse.error === 'integration_invalid') {
      result.errorType = 'configuration_error';
    } else if (errorResponse.error === 'default_config_failed') {
      result.errorType = 'default_config_failed';
    } else if (errorResponse.error === 'integration_disabled') {
      result.errorType = 'disabled';
    } else if (errorResponse.error === 'model_error') {
      result.errorType = 'model_error';
    } else if (errorResponse.error === 'rate_limited') {
      result.errorType = 'rate_limited';
    }
  } catch (e) {}

  return result;
};

/**
 * Handles specific error response structure from the backend
 *
 * @param errorData - The error data from the backend response
 * @param t - Translation function
 * @returns Formatted error message
 */
export const handleBackendErrorResponse = (errorData: any, t: any): string => {
  let errorMessage = t('ai-service-unavailable');

  switch (errorData.error) {
    case 'rate_limited':
      errorMessage = t('ai-rate-limit-exceeded');
      break;
    case 'integration_invalid':
      // Check if it's a default config error directly here for backward compatibility
      if (errorData.default_config === true) {
        errorMessage = t('ai-default-integration-config-error');
      } else {
        errorMessage = t('ai-integration-config-error');
      }
      break;
    case 'Default_config_failed':
      errorMessage = t('ai-default-integration-config-error');
      break;
    case 'integration_disabled':
      errorMessage = t('ai-integration-disabled');
      break;
    case 'model_error':
      errorMessage = t('ai-model-error', {
        model: errorData.model || formatProviderName(errorData.model_provider),
        error: errorData.details || errorData.message,
      });
      break;
  }

  return errorMessage;
};

/**
 * Handles general error scenarios using the extracted error info
 *
 * @param errorInfo - The extracted error information
 * @param t - Translation function
 * @returns Formatted error message
 */
export const handleGeneralError = (errorInfo: AIErrorInfo, t: any): string => {
  let errorMessage = t('ai-service-unavailable');
  switch (errorInfo.errorType) {
    case 'default_config_failed':
      errorMessage = t('ai-default-integration-config-error');
      break;
    case 'configuration_error':
      errorMessage = t('ai-integration-config-error');
      break;
    case 'disabled':
      errorMessage = t('ai-integration-disabled');
      break;
    case 'model_error': {
      const displayModel = errorInfo.modelName || errorInfo.integrationName;
      errorMessage = t('ai-model-error', {
        model: displayModel,
        error: errorInfo.errorDetails || t('ai-service-unavailable'),
      });
      break;
    }
    case 'rate_limited':
      errorMessage = t('ai-rate-limit-exceeded');
      break;
    default:
      // keep default ai-service-unavailable message
      break;
  }

  return errorMessage;
};
